import { useCurrentCompany, useFeatureFlag } from '@bonusly/who-am-i';

import { useUserContentDismissal } from '../profile/user-content-dismissal';
import { TRIAL_WELCOME_DISMISSAL } from '../recognition/trial-welcome';

/** sessionStorage key set when Welcome V2 signup completes (founding admin only). */
const SIGNUP_SESSION_KEY = 'bizy_onboarding_signup_session';

/** Mark this tab as the founding admin's signup session before homepage handoff. */
export const markTrialSignupSession = (): void => {
	try {
		sessionStorage.setItem(SIGNUP_SESSION_KEY, '1');
	} catch {
		// Private mode / quota — panel may auto-open as before.
	}
};

const isTrialSignupSession = (): boolean => {
	try {
		return sessionStorage.getItem(SIGNUP_SESSION_KEY) === '1';
	} catch {
		return false;
	}
};

/**
 * Whether the founding admin's signup session still awaits `trial_welcome_sent`.
 * Keeps the Bizy panel from auto-opening over the welcome demo (Meet Bizy promo counterpart).
 *
 * `isResolved` is reported apart from `isPending` so a caller latching a
 * once-per-load decision can tell "no answer yet" from "answered: the demo owns
 * this screen".
 */
const useTrialWelcomeDemoTakeover = (): { isResolved: boolean; isPending: boolean } => {
	const isTrial = !!useCurrentCompany()?.isTrial;
	const isBizyLedOnboardingEnabled = useFeatureFlag('plg-bizy-led-onboarding');
	const isEligible = isTrial && isBizyLedOnboardingEnabled && isTrialSignupSession();
	const { loading, data } = useUserContentDismissal({ ...TRIAL_WELCOME_DISMISSAL, enabled: isEligible });

	if (!isEligible) return { isResolved: true, isPending: false };
	if (loading) return { isResolved: false, isPending: false };
	return { isResolved: true, isPending: !data?.dismissed_at };
};

/**
 * True while the demo may still appear, including before we know. Loading counts
 * as pending so the panel can't open before we know the dismissal state.
 */
export const useHasPendingTrialWelcomeDemo = (): boolean => {
	const { isResolved, isPending } = useTrialWelcomeDemoTakeover();
	return !isResolved || isPending;
};

/** Whether the demo's dismissal record has answered, either way. */
export const useIsTrialWelcomeDemoResolved = (): boolean => useTrialWelcomeDemoTakeover().isResolved;
